import { dayjs, getAppTimezone } from '@lib/dayjs'
import { dateFormats } from '@lib/formatters'

// Serializable date range used at picker / filter boundaries.
// Values are UTC ISO 8601 strings — safe for Redux, URL params, IDB.
export type IsoDateRange = { from?: string | null; to?: string | null }

// Picker Date (browser-local wall-clock) -> backend UTC ISO, interpreted in the user's tz.
//
// Example — user tz: "Asia/Dubai" (UTC+4), browser tz: "America/Los_Angeles":
//   input:  new Date(2026, 3, 19, 0, 0, 0)        // 2026-04-19 00:00 (browser-local)
//   output: "2026-04-18T20:00:00.000Z"            // 2026-04-19 00:00 Dubai -> UTC
export const pickerDateToBackendIso = (date: Date): string => {
  const wall = dayjs(date).format('YYYY-MM-DDTHH:mm:ss')

  return dayjs.tz(wall, getAppTimezone()).toISOString()
}

// Backend UTC ISO -> picker Date (browser-local wall-clock) matching the user-tz rendering.
//
// Example — user tz: "Asia/Dubai" (UTC+4), browser tz: "America/Los_Angeles":
//   input:  "2026-04-18T20:00:00.000Z"            // 2026-04-19 00:00 Dubai
//   output: new Date(2026, 3, 19, 0, 0, 0)        // 2026-04-19 00:00 (browser-local)
export const backendIsoToPickerDate = (iso: string | Date): Date => {
  const t = dayjs.utc(iso).tz(getAppTimezone())

  return new Date(t.year(), t.month(), t.date(), t.hour(), t.minute(), t.second(), t.millisecond())
}

// Dayjs (in any tz) -> picker Date whose wall-clock matches the Dayjs rendered in the user's tz.
// Used for feeding presets (Today, Yesterday, ...) into react-day-picker.
//
// Example — user tz: "Asia/Dubai", browser tz: "America/Los_Angeles", preset "Today":
//   input:  dayjs().tz("Asia/Dubai").startOf("day")   // e.g. 2026-04-20 00:00 Dubai
//   output: new Date(2026, 3, 20, 0, 0, 0)            // 2026-04-20 00:00 (browser-local)
export const tzDayjsToPickerDate = (d: dayjs.Dayjs): Date => {
  const t = d.tz(getAppTimezone())

  return new Date(t.year(), t.month(), t.date(), t.hour(), t.minute(), t.second(), t.millisecond())
}

// Date-only fields ("date of birth", "expires on") are a bare `YYYY-MM-DD`: no time, no timezone.
// The UTC converters above would shift the day for offsets that cross midnight.
const DATE_ONLY_FORMAT = 'YYYY-MM-DD'
const ISO_DATE_START = /^\d{4}-\d{2}-\d{2}/

export const toDateOnly = (value?: string | null): string => {
  if (typeof value !== 'string' || !ISO_DATE_START.test(value)) return ''

  const parsed = dayjs.utc(value)

  return parsed.isValid() ? parsed.format(DATE_ONLY_FORMAT) : ''
}

// Answers are resubmitted under `clear_missing: true`, so text that is not a date must survive as-is:
// blanking it here would clear a value the admin never touched.
export const toDateOnlyOrRaw = (value: unknown): string => {
  if (typeof value !== 'string') return ''

  const trimmed = value.trim()
  const isoDate = toDateOnly(trimmed)
  // dayjs rolls an impossible date over: 1990-02-31 parses as 1990-03-03.
  const survivedRoundTrip = Boolean(isoDate) && trimmed.startsWith(isoDate)

  return survivedRoundTrip ? isoDate : trimmed
}

export const formatDateOnly = (value: unknown): string => {
  const dateOnly = toDateOnlyOrRaw(value)
  const isRealDate = Boolean(dateOnly) && toDateOnly(dateOnly) === dateOnly

  return isRealDate ? dayjs(dateOnly).format(dateFormats.short) : dateOnly
}

export const dateOnlyToPickerDate = (value?: string | null): Date | undefined => {
  const dateOnly = toDateOnly(value)

  return dateOnly ? dayjs(dateOnly).toDate() : undefined
}

export const pickerDateToDateOnly = (date: Date): string => dayjs(date).format(DATE_ONLY_FORMAT)
